import { createRebaseClient } from "@rebasepro/client";

// Where the API lives, in order of precedence:
//
//   1. VITE_API_URL — injected by `rebase dev` (it picks a deterministic
//      per-project port), and available to bake a cross-origin API if one is
//      ever needed.
//   2. The page's own origin — the backend serves this SPA and the API from a
//      single origin, so the correct base URL is whatever host was loaded.
//      Deriving it at runtime instead of baking it at build time is what keeps
//      a domain change from bricking the client: the app broke exactly that way
//      when the tenant moved from *.apps.rebase.pro to *.rebase.website and the
//      bundle kept calling the dead hostname.
//   3. localhost:3001 — only for a Vite dev server with nothing injected.
//
// Case 3 keys off the PORT, not the hostname. Keying off the hostname meant
// every localhost page was assumed to be a dev server, so a production build
// served from localhost:3110 called localhost:3001 and failed on every request
// — which forced local testing onto a LAN IP for no reason. A dev server is
// identified by the port Vite actually uses; anything else on localhost is a
// real server that serves its own API.
const DEV_SERVER_PORTS = new Set(["5173", "5174", "5175", "4173"]);

// Hosts that mean "this visitor's own machine". A base URL pointing at one of
// these is only ever correct for a page that is itself being served there.
const LOOPBACK_HOSTS = new Set(["localhost", "127.0.0.1", "::1", "[::1]", "0.0.0.0"]);

const loc = typeof window === "undefined" ? undefined : window.location;
const isViteDevServer = Boolean(loc && DEV_SERVER_PORTS.has(loc.port));

function hostnameOf(url: string): string | undefined {
    try {
        // A relative value ("/api") resolves against the page and is therefore
        // same-origin by construction — never loopback-by-accident.
        return new URL(url, loc?.href ?? "http://localhost").hostname;
    } catch {
        return undefined;
    }
}

const isLoopback = (host: string | undefined) => host !== undefined && LOOPBACK_HOSTS.has(host.toLowerCase());

// VITE_API_URL is a BUILD-TIME constant, so it is only ever right for the
// machine that ran the build. The root `.env` set it to http://localhost:3001
// for the dev server, and `frontend/vite.config.ts` points `envDir` at that
// same file for a production build too — so `rebase build` compiled the
// developer's own laptop into the deployed bundle and every request from every
// visitor of https://rebase-growth.rebase.website went to *their* localhost:3001
// and failed. (`scripts/prepare-deploy.mjs` blanked it via `.env.production`,
// but that is the source/Dockerfile path this app stopped using when it moved
// to the managed runtime — the cancellation was left behind with it.)
//
// So: drop an injected loopback URL whenever the page itself is not on
// loopback. That pairing is never correct and only ever comes from a leaked dev
// value, and the guard travels with the bundle — it holds no matter which build
// path or env file a future deploy goes through.
const injected = import.meta.env.VITE_API_URL as string | undefined;
const pageIsLoopback = !loc || isLoopback(loc.hostname);
const strandedLoopback = Boolean(injected) && !pageIsLoopback && isLoopback(hostnameOf(injected as string));

if (strandedLoopback) {
    console.warn(
        `[rebase] ignoring VITE_API_URL=${injected} — it points at this browser's own machine `
            + `while the page is served from ${loc?.origin}. Using the page origin instead. `
            + "A loopback API URL was baked into a production build; unset VITE_API_URL where it was built."
    );
}

const usableInjected = strandedLoopback ? undefined : injected;

/**
 * Where the API is. Exported for the one call the client cannot make itself: a
 * download, whose body is a file and not JSON.
 */
export const API_URL = usableInjected || (loc && !isViteDevServer ? loc.origin : "http://localhost:3001");

// Refresh token lives in an httpOnly cookie (XSS-safe); the backend opts in
// via `auth.cookieAuth`.
export const client = createRebaseClient({
    baseUrl: API_URL,
    auth: { authFlowMode: "cookie" }
});
